import type { CompositionV3Ent, CompositionV3Req } from '@revelio/data-access';

import type {
  Filter,
  FilterId,
  TreeSelection,
} from '../../../../filters/types/filters';
import { isTreeFilter } from '../../../../filters/utils/common';
import { mergeDrilldownIntoCompositionV3Filters } from '../../../../filters/utils/composition-v3/merge-drilldown-into-composition-v3-filters';
import { getNumericIdFromNestedId } from '../../../../filters/utils/get-numeric-id-from-nested-id';
import { resolveV3EntForSelection } from '../../../../filters/utils/get-v3-entities';
import { drilldownEntryToFilter } from '../../../Chart/utils/composition-drilldown';
import type { IcicleTaxonomy } from './taxonomies/types';

const MAX_ICICLE_TIERS = 3;

const OVERLAPPING_ICICLE_TIERS = 2;

export const icicleTiersBelowRoot = (
  overlapping: boolean,
  tiersBelowRoot: number | undefined
) =>
  tiersBelowRoot ?? (overlapping ? OVERLAPPING_ICICLE_TIERS : MAX_ICICLE_TIERS);

export const ICICLE_BREAKDOWN_TOP_N = 50;

export type TaxonomyIcicleEntity = {
  key: string;
  name: string;
  rootDepth: number;
};

export type TaxonomyIcicleDrill = { levelId: string; id: string; name: string };

export type TaxonomyIcicleChip = {
  key: string;
  name: string;
  levelId: string;
  id: string;
};

export type PageFilterSelection = { levelId: string; id: string };

/** One composition request: the tier it breaks down and the drilled node it is narrowed to, if any. */
export type TaxonomyIcicleRequest = {
  key: string;
  depth: number;
  scope: TaxonomyIcicleDrill | null;
};

export function getIcicleSelectionDepth(
  taxonomy: IcicleTaxonomy,
  levelIds: readonly string[],
  levelId: string
): number | null {
  const ranked = taxonomy.rankedLevelIds ?? levelIds;
  const rank = ranked.indexOf(levelId);
  if (rank === -1) return null;
  const depth =
    levelIds.filter((ladderLevelId) => {
      const ladderRank = ranked.indexOf(ladderLevelId);
      return ladderRank !== -1 && ladderRank <= rank;
    }).length - 1;
  return depth >= 0 ? depth : null;
}

/**
 * The entity a chip is requested as. A chip on the icicle's own ladder takes
 * the level the taxonomy resolves for it once its tree is known (the skill
 * ladder is positional, so that level only exists then); any other chip is
 * the entity its own filter would send.
 */
export const resolveTaxonomyIcicleChipEntity = (
  taxonomy: IcicleTaxonomy,
  levelIds: readonly string[] | null,
  chip: Pick<TreeSelection, 'id' | 'levelId'>
): CompositionV3Ent | null => {
  if (levelIds === null || !levelIds.includes(chip.levelId)) {
    return resolveV3EntForSelection(chip);
  }
  const entKind = taxonomy.entKind(chip.levelId);
  const id = getNumericIdFromNestedId(chip.id);
  return entKind === null || id === null
    ? null
    : { EntType: entKind, EntID: id };
};

export const pageFilterSelections = (
  effectiveFilters: readonly Filter[],
  filterId: FilterId
): PageFilterSelection[] =>
  effectiveFilters.flatMap((filter) =>
    filter.id === filterId &&
    isTreeFilter(filter) &&
    filter.isPrimary !== true &&
    filter.isNot !== true
      ? filter.selections.flatMap((selection) => {
          const id = getNumericIdFromNestedId(selection.id);
          return id === null
            ? []
            : [{ levelId: selection.levelId, id: String(id) }];
        })
      : []
  );

export const arePageFilterSelectionsEqual = (
  previous: readonly PageFilterSelection[],
  next: readonly PageFilterSelection[]
) =>
  previous.length === next.length &&
  previous.every(
    (selection, index) =>
      selection.levelId === next[index]?.levelId &&
      selection.id === next[index]?.id
  );

const scopesRootDepth = (scopes: readonly { depth: number }[]): number => {
  const [only] = scopes;
  if (only === undefined) return -1;
  if (scopes.length === 1) return only.depth;
  return Math.min(...scopes.map((scope) => scope.depth)) - 1;
};

export function resolveTaxonomyIcicleEntities({
  taxonomy,
  levelIds,
  chips,
  filterSelections,
}: {
  taxonomy: IcicleTaxonomy;
  levelIds: readonly string[];
  chips: readonly TaxonomyIcicleChip[];
  filterSelections: readonly PageFilterSelection[];
}): TaxonomyIcicleEntity[] {
  const depthOf = (levelId: string) =>
    getIcicleSelectionDepth(taxonomy, levelIds, levelId);
  const filterScopes = filterSelections.flatMap(({ levelId }) => {
    const depth = depthOf(levelId);
    return depth === null ? [] : [{ depth }];
  });
  const filterRootDepth = scopesRootDepth(filterScopes);
  return chips.map(({ key, name, levelId }) => ({
    key,
    name,
    rootDepth: depthOf(levelId) ?? filterRootDepth,
  }));
}

/**
 * Rolled-up taxonomies: one request at the leaf of the window is enough,
 * every tier above it being the sum of its leaves. A drill asks again under
 * the drilled node, whose leaves the chip-level request may have left out.
 */
const planRolledUpRequests = ({
  lastDepth,
  rootDepth,
  drill,
  drillDepth,
  tiersBelowRoot,
}: {
  lastDepth: number;
  rootDepth: number;
  drill: TaxonomyIcicleDrill | null;
  drillDepth: number;
  tiersBelowRoot: number;
}): TaxonomyIcicleRequest[] => {
  const leafDepthBelow = (depth: number) =>
    Math.min(depth + tiersBelowRoot, lastDepth);
  const chipLeaf = {
    key: CHIP_LEAF_KEY,
    depth: leafDepthBelow(rootDepth),
    scope: null,
  };
  if (drill === null || drillDepth === -1) return [chipLeaf];
  return [
    chipLeaf,
    { key: 'drill-leaf', depth: leafDepthBelow(drillDepth), scope: drill },
  ];
};

const chipTierKey = (tier: number) => `chip-tier-${tier}`;

const CHIP_LEAF_KEY = 'chip-leaf';

/**
 * Whether a request rides with the page's cells rather than its groups: a
 * chip's leaves, or any tier of it below the first.
 */
export const isChipCellsRequest = (request: TaxonomyIcicleRequest): boolean =>
  request.scope === null &&
  (request.key === CHIP_LEAF_KEY ||
    (request.key.startsWith('chip-tier-') && request.key !== chipTierKey(1)));

/**
 * Overlapping taxonomies: every drawn tier is requested on its own, nothing
 * being derivable from the tier below, so the chip requests cover the tiers
 * under every chip, whatever its depth. Once drilled, the chip tiers above the
 * drilled node are no longer drawn: the plan keeps the drilled node's own tier
 * (its share sizes the new root) and asks for the tiers beneath it under the
 * drilled node, whose children the chip tiers may have left out.
 */
const planOverlappingRequests = ({
  lastDepth,
  rootDepths,
  drill,
  drillDepth,
  tiersBelowRoot,
}: {
  lastDepth: number;
  rootDepths: readonly number[];
  drill: TaxonomyIcicleDrill | null;
  drillDepth: number;
  tiersBelowRoot: number;
}): TaxonomyIcicleRequest[] => {
  const tiersBelow = (depth: number): number[] => {
    const tiers: number[] = [];
    for (
      let tier = depth + 1;
      tier <= Math.min(depth + tiersBelowRoot, lastDepth);
      tier++
    ) {
      tiers.push(tier);
    }
    return tiers.length > 0 ? tiers : [Math.max(depth, 0)];
  };
  const chipTiers = [...new Set(rootDepths.flatMap(tiersBelow))].sort(
    (a, b) => a - b
  );
  const chipRequests: TaxonomyIcicleRequest[] = chipTiers.map(
    (depth, tier) => ({ key: chipTierKey(tier + 1), depth, scope: null })
  );

  if (drill === null || drillDepth === -1) return chipRequests;
  const drillRequests = tiersBelow(drillDepth)
    .filter((depth) => depth > drillDepth)
    .map(
      (depth, tier): TaxonomyIcicleRequest => ({
        key: `drill-tier-${tier + 1}`,
        depth,
        scope: drill,
      })
    );
  return [
    ...chipRequests.filter(({ depth }) => depth === drillDepth),
    ...drillRequests,
  ];
};

export function planTaxonomyIcicleRequests({
  overlapping,
  levelIds,
  entities,
  drill,
  tiersBelowRoot,
}: {
  overlapping: boolean;
  levelIds: readonly string[];
  entities: readonly TaxonomyIcicleEntity[];
  drill: TaxonomyIcicleDrill | null;
  tiersBelowRoot?: number;
}): TaxonomyIcicleRequest[] {
  const rootDepths =
    entities.length === 0 ? [-1] : entities.map((entity) => entity.rootDepth);
  const shared = {
    lastDepth: levelIds.length - 1,
    drill,
    drillDepth: drill === null ? -1 : levelIds.indexOf(drill.levelId),
    tiersBelowRoot: icicleTiersBelowRoot(overlapping, tiersBelowRoot),
  };
  return overlapping
    ? planOverlappingRequests({ ...shared, rootDepths })
    : planRolledUpRequests({
        ...shared,
        rootDepth: Math.max(-1, ...rootDepths),
      });
}

export function buildTaxonomyIcicleRequest({
  baseRequest,
  taxonomy,
  levelIds,
  depth,
  drill,
}: {
  baseRequest: CompositionV3Req;
  taxonomy: IcicleTaxonomy;
  levelIds: readonly string[];
  depth: number | null;
  drill: TaxonomyIcicleDrill | null;
}): CompositionV3Req | null {
  const levelId = depth === null ? undefined : levelIds[depth];
  const entKind = levelId === undefined ? null : taxonomy.entKind(levelId);
  if (entKind === null) return null;
  return {
    ...baseRequest,
    filters:
      drill === null
        ? baseRequest.filters
        : mergeDrilldownIntoCompositionV3Filters(
            baseRequest.filters,
            drilldownEntryToFilter([
              {
                level: drill.levelId,
                id: drill.id,
                name: drill.name,
                segment: taxonomy.segment,
              },
            ])
          ),
    breakdown_options: {
      ...baseRequest.breakdown_options,
      [taxonomy.breakdownLevelField]: entKind,
      [taxonomy.topNField]: ICICLE_BREAKDOWN_TOP_N,
    },
  };
}
